import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AGENT_STEP_LABELS } from '../agent/tools';

interface Props {
  toolName: string;
  args: Record<string, any>;
  status: 'running' | 'success' | 'error';
  result?: any;
  errorMessage?: string;
  durationMs?: number;
}

function headerText(toolName: string, status: Props['status'], result: any, errorMessage?: string): string {
  const labels = AGENT_STEP_LABELS[toolName];
  if (status === 'running') return labels?.running || `🛠️ ${toolName} を実行中…`;
  if (status === 'error') return `❌ ${toolName}: ${errorMessage || 'エラー'}`;
  if (labels?.success) return labels.success(result);
  return `✅ ${toolName} 完了`;
}

export default function AgentActivityCard({ toolName, args, status, result, errorMessage, durationMs }: Props) {
  const [expanded, setExpanded] = useState(false);
  const headerColor = status === 'error' ? '#b91c1c' : status === 'success' ? '#166534' : '#0369a1';

  return (
    <Box
      sx={{
        mx: 1.5,
        my: 0.5,
        border: '1px solid #e2e8f0',
        borderRadius: 1,
        overflow: 'hidden',
        background: '#f8fafc',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.2,
          py: 0.8,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Box display="flex" alignItems="center" gap={0.8} sx={{ minWidth: 0 }}>
          {status === 'running' && <CircularProgress size={14} thickness={6} />}
          <Typography variant="caption" sx={{ fontWeight: 700, color: headerColor }}>
            {headerText(toolName, status, result, errorMessage)}
          </Typography>
          {durationMs != null && status !== 'running' && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {durationMs}ms
            </Typography>
          )}
        </Box>
        <IconButton size="small" sx={{ p: 0.2 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1.2, pb: 1, borderTop: '1px dashed #e2e8f0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mt: 0.6 }}>
            引数
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              fontSize: 11,
              background: '#fff',
              p: 1,
              borderRadius: 0.5,
              border: '1px solid #e2e8f0',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {JSON.stringify(args, null, 2)}
          </Box>

          {(status === 'success' || status === 'error') && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mt: 0.6 }}>
                {status === 'error' ? 'エラー' : '結果'}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  fontSize: 11,
                  background: '#fff',
                  p: 1,
                  borderRadius: 0.5,
                  border: '1px solid #e2e8f0',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  maxHeight: 280,
                }}
              >
                {status === 'error'
                  ? errorMessage
                  : JSON.stringify(result, null, 2)?.slice(0, 4000)}
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
